import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Check, FileText, GitMerge, Brain, ShieldCheck } from 'lucide-react'
import { Nav } from '../components/Nav'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'
import {
  ENTERPRISE_FEATURES,
  MATRIX,
  PRICING_SEO,
  TIERS,
  type Access,
} from '../data/pricing'

// ── Enterprise icons (mapped onto ENTERPRISE_FEATURES by index) ───────────────

const ENT_ICONS = [
  <GitMerge size={18} />,
  <FileText size={18} />,
  <Brain size={18} />,
  <ShieldCheck size={18} />,
]

const ENT_FEATURES = ENTERPRISE_FEATURES.map((label, i) => ({
  icon: ENT_ICONS[i],
  label,
}))

// ── Cell renderer ─────────────────────────────────────────────────────────────

function Cell({ col, access, textValues }: {
  col: 'free' | 'pro' | 'ent'
  access: Access
  textValues?: [string, string, string]
}) {
  if (access === 'text') {
    const val = col === 'free' ? textValues![0] : col === 'pro' ? textValues![1] : textValues![2]
    return <td className="text-center py-2.5 px-3 text-xs text-brand-charcoal">{val}</td>
  }
  const included =
    access === 'all' ||
    (access === 'pro' && (col === 'pro' || col === 'ent')) ||
    (access === 'ent' && col === 'ent')

  return (
    <td className="text-center py-2.5 px-3">
      {included ? (
        <span className="inline-flex items-center justify-center text-emerald-600 font-semibold text-xs">
          <Check size={13} strokeWidth={3} />
        </span>
      ) : (
        <span className="text-slate-300 text-sm">—</span>
      )}
    </td>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PricingPage() {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans">
      <Helmet>
        <title>{PRICING_SEO.title}</title>
        <meta name="description" content={PRICING_SEO.description} />
      </Helmet>
      <Nav />

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12">

        {/* Hero */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-medium tracking-tight">Simple pricing</h1>
          <p className="text-sm text-slate-500">Start free. Upgrade when you need more.</p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`rounded-xl bg-white flex flex-col p-6 ${
                tier.highlight
                  ? 'border-2 border-brand-orange shadow-sm'
                  : 'border border-slate-200'
              }`}
            >
              {tier.badge && (
                <div className="text-[11px] font-semibold tracking-wider text-brand-orange mb-2">
                  {tier.badge}
                </div>
              )}
              <h2 className="text-lg font-medium mb-0.5">{tier.name}</h2>
              <p className="text-sm text-slate-500 mb-1">{tier.sub}</p>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">{tier.tagline}</p>
              <ul className="space-y-2.5 flex-1 mb-5">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-brand-charcoal">
                    <span className="mt-0.5 shrink-0 text-emerald-600">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/"
                className={`block text-center rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  tier.highlight
                    ? 'bg-brand-orange text-white hover:bg-brand-orange/90'
                    : 'border border-brand-orange text-brand-orange hover:bg-brand-orange/5'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Enterprise banner */}
        <section className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-4">
          <h2 className="text-base font-medium">
            Managing IND submissions for your team?
          </h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto leading-relaxed">
            Chrona Enterprise provides cross-document consistency checks, FDA
            response intelligence, and 21 CFR Part 11 compliance for your
            entire regulatory workflow.
          </p>
          <a
            href="https://chronabio.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm font-medium text-brand-orange hover:underline"
          >
            Learn more →
          </a>
          <div className="flex flex-wrap justify-center gap-8 pt-2">
            {ENT_FEATURES.map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1.5">
                <div className="h-9 w-9 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center text-slate-500">
                  {f.icon}
                </div>
                <span className="text-[11px] text-slate-500 text-center max-w-[80px] leading-tight">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Feature matrix */}
        <section className="space-y-4">
          <div className="text-center space-y-1">
            <h2 className="text-base font-medium">Compare all features</h2>
            <p className="text-sm text-slate-500">Everything included in each tier.</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 tracking-wide w-1/2">
                    Feature
                  </th>
                  <th className="text-center py-3 px-3 text-xs font-medium text-slate-500 tracking-wide w-[70px]">
                    Free
                  </th>
                  <th className="text-center py-3 px-3 text-xs font-medium text-brand-orange tracking-wide w-[70px] bg-brand-orange/5">
                    Pro
                  </th>
                  <th className="text-center py-3 px-3 text-xs font-medium text-slate-500 tracking-wide w-[70px]">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((section) => (
                  <Fragment key={section.category}>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td
                        colSpan={4}
                        className="py-2 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                      >
                        {section.category}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr
                        key={row.feature}
                        className="border-b border-slate-100 last:border-0"
                      >
                        <td className="py-2.5 px-4">
                          <span className="text-sm text-brand-charcoal">{row.feature}</span>
                          {row.detail && (
                            <span className="block text-[11px] text-slate-400 mt-0.5">
                              {row.detail}
                            </span>
                          )}
                        </td>
                        <Cell col="free" access={row.access} textValues={row.textValues} />
                        <td className="bg-brand-orange/[0.03] text-center py-2.5 px-3">
                          {row.access === 'text' ? (
                            <span className="text-xs text-brand-charcoal">{row.textValues![1]}</span>
                          ) : row.access === 'all' || row.access === 'pro' ? (
                            <span className="inline-flex items-center justify-center text-emerald-600">
                              <Check size={13} strokeWidth={3} />
                            </span>
                          ) : (
                            <span className="text-slate-300 text-sm">—</span>
                          )}
                        </td>
                        <Cell col="ent" access={row.access} textValues={row.textValues} />
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </main>

      <TermsPrivacyFooter />
    </div>
  )
}
